/**
 * WordPress dependencies
 */
import { _x } from '@safe-wordpress/i18n';
import { useMemo, useState } from '@safe-wordpress/element';
import { useMergeRefs } from '@safe-wordpress/compose';
import {
	Grid,
	BorderControl,
	type WordPressComponentProps,
} from '@safe-wordpress/components';

/**
 * Internal dependencies
 */
import { contextConnect } from '../../context';
import BorderBoxControlVisualizer from '../border-box-control-visualizer';
import { useBorderBoxControlSplitControls } from './hook';
import type { SplitControlsProps } from '../types';
import type { BorderControlProps } from '../../border-control/types';

const BorderBoxControlSplitControls = (
	props: WordPressComponentProps< SplitControlsProps, 'div' >,
	forwardedRef: React.ForwardedRef< unknown >
) => {
	const {
		centeredClassName,
		colors,
		disableCustomColors,
		enableAlpha,
		enableStyle,
		onChange,
		popoverPlacement,
		popoverOffset,
		rightAlignedClassName,
		size = 'default',
		value,
		readonlyBorders,
		__experimentalIsRenderedInSidebar,
		...otherProps
	} = useBorderBoxControlSplitControls( props );

	// Use internal state instead of a ref to make sure that the component
	// re-renders when the popover's anchor updates.
	const [ popoverAnchor, setPopoverAnchor ] = useState< Element | null >(
		null
	);

	// Memoize popoverProps to avoid returning a new object every time.
	const popoverProps: BorderControlProps[ '__unstablePopoverProps' ] =
		useMemo(
			() =>
				popoverPlacement
					? {
							placement: popoverPlacement,
							offset: popoverOffset,
							anchor: popoverAnchor,
							shift: true,
					  }
					: undefined,
			[ popoverPlacement, popoverOffset, popoverAnchor ]
		);

	const sharedBorderControlProps = {
		colors,
		disableCustomColors,
		enableAlpha,
		enableStyle,
		isCompact: true,
		__experimentalIsRenderedInSidebar,
		size,
		__shouldNotWarnDeprecated36pxSize: true,
	};

	const mergedRef = useMergeRefs( [ setPopoverAnchor, forwardedRef ] );

	return (
		<Grid { ...otherProps } ref={ mergedRef } gap={ 3 }>
			<BorderBoxControlVisualizer value={ value } size={ size } />
			<BorderControl
				className={ centeredClassName }
				readonlyBorder={ readonlyBorders?.top }
				hideLabelFromVision
				label={ _x( 'Top border', 'text', 'nelio-ab-testing' ) }
				onChange={ ( newBorder ) => onChange( newBorder, 'top' ) }
				__unstablePopoverProps={ popoverProps }
				value={ value?.top }
				{ ...sharedBorderControlProps }
			/>
			<BorderControl
				hideLabelFromVision
				readonlyBorder={ readonlyBorders?.left }
				label={ _x( 'Left border', 'text', 'nelio-ab-testing' ) }
				onChange={ ( newBorder ) => onChange( newBorder, 'left' ) }
				__unstablePopoverProps={ popoverProps }
				value={ value?.left }
				{ ...sharedBorderControlProps }
			/>
			<BorderControl
				className={ rightAlignedClassName }
				readonlyBorder={ readonlyBorders?.right }
				hideLabelFromVision
				label={ _x( 'Right border', 'text', 'nelio-ab-testing' ) }
				onChange={ ( newBorder ) => onChange( newBorder, 'right' ) }
				__unstablePopoverProps={ popoverProps }
				value={ value?.right }
				{ ...sharedBorderControlProps }
			/>
			<BorderControl
				className={ centeredClassName }
				readonlyBorder={ readonlyBorders?.bottom }
				hideLabelFromVision
				label={ _x( 'Bottom border', 'text', 'nelio-ab-testing' ) }
				onChange={ ( newBorder ) => onChange( newBorder, 'bottom' ) }
				__unstablePopoverProps={ popoverProps }
				value={ value?.bottom }
				{ ...sharedBorderControlProps }
			/>
		</Grid>
	);
};

const ConnectedBorderBoxControlSplitControls = contextConnect(
	BorderBoxControlSplitControls,
	'BorderBoxControlSplitControls'
);
export default ConnectedBorderBoxControlSplitControls;
